import {
  combine,
  createDomain,
  createEffect,
  createEvent,
  sample,
} from "effector";
import { articlesApi } from "../api";
import { ArticleListFilters, GetArticlesResponse } from "../types";
import { PAGINATION_LIMIT } from "../../../shared/configs/consts";
import { startOfDay, endOfDay, format, parseISO } from "date-fns";
import { createGate } from "effector-react";
import { interval } from "patronum";
import { notifications } from "@mantine/notifications";

const Gate = createGate<{ campaignId?: number }>();

const domain = createDomain();

const updateFilter = createEvent<Partial<ArticleListFilters>>();
const applyFilters = createEvent();
const resetFilters = createEvent();
const $filters = domain.createStore<ArticleListFilters>({});
const $campaignId = domain.createStore<number | null>(null);
const deleteArticleById = createEvent<string>();
const deleteArticleByIdFx = createEffect(articlesApi.deleteById);

sample({
  clock: Gate.state,
  fn: (state) => state.campaignId ?? null,
  target: $campaignId,
});

sample({
  clock: resetFilters,
  fn: () => ({}),
  target: $filters,
});

sample({
  source: $filters,
  clock: updateFilter,
  fn: (filters, payload) => ({ ...filters, ...payload }),
  target: $filters,
});

const $page = domain.createStore(1);
const setPage = createEvent<number>();

sample({
  clock: setPage,
  target: $page,
});

const getList = createEvent();
const fetchListFx = createEffect(articlesApi.getList);
const $data = domain.createStore<GetArticlesResponse | null>(null);

const $isFetched = domain
  .createStore(false)
  .on(fetchListFx.finally, () => true);

const $isLoading = combine(
  {
    isFetched: $isFetched,
    pending: fetchListFx.pending,
  },
  ({ isFetched, pending }) => !isFetched && pending
);

const $list = $data.map((data) =>
  (data?.data ?? []).map((row) => ({
    ...row,
    date: row.date ? format(parseISO(row.date), "dd/MM/yyyy HH:mm") : undefined,
    createdAt: format(parseISO(row.createdAt), "dd/MM/yyyy HH:mm"),
  }))
);
const $totalPages = $data.map((data) =>
  Math.ceil((data?.meta?.total ?? 0) / PAGINATION_LIMIT)
);

sample({
  clock: [Gate.status, $page, applyFilters, resetFilters, $campaignId],
  source: {
    campaignId: $campaignId,
  },
  target: getList,
});

sample({
  source: {
    page: $page,
    filters: $filters,
    campaignId: $campaignId,
  },
  clock: getList,
  filter: ({ campaignId }) => Boolean(campaignId),
  fn: ({ page, filters, campaignId }) => {
    return {
      ...filters,
      createdFrom: filters.createdFrom
        ? startOfDay(new Date(filters.createdFrom)).toISOString()
        : undefined,
      createdTo: filters.createdTo
        ? endOfDay(new Date(filters.createdTo)).toISOString()
        : undefined,
      limit: PAGINATION_LIMIT,
      offset: (page - 1) * PAGINATION_LIMIT,
      campaignId,
    };
  },
  target: fetchListFx,
});

sample({
  clock: fetchListFx.doneData,
  target: $data,
});

sample({
  clock: deleteArticleById,
  target: deleteArticleByIdFx,
});

sample({
  clock: deleteArticleByIdFx.doneData,
  filter: (data) => Boolean(data.campaignId),
  fn: (data) => {
    notifications.show({
      title: "Article deleted",
      message: "Article deleted successfully",
      color: "green",
    });
    return {
      campaignId: data.campaignId,
      page: 1,
    };
  },
  target: getList,
});

interval({
  start: Gate.open,
  stop: Gate.close,
  timeout: 10000,
}).tick.watch(getList);

export const articlesEntityModel = {
  $page,
  $totalPages,
  $list,
  $filters,
  applyFilters,
  resetFilters,
  setPage,
  updateFilter,
  getList,
  Gate,
  deleteArticleById,
  deleteArticleByIdFx,
  $isLoading,
};
